import { attributes, dia, shapes, ui, util } from "@clientio/rappid";
import { PreferenceStore } from "../../services/preference-store";
import { BaseNode, BaseView } from "./base-node";
import { ElementController } from "./element-controller";
import NamedElement from "./named-element";
import { deselectAll, difference, removeStaticAttributes } from "./utils";
import RappidContext from "../../rappid/rappid-context";

// TODO there are still some hardcoded colors everywhere, replace them by PreferenceStore.INSTANCE.theme.model variable"
export const DEFAULT_NAME_COLOR_LIGHT = "#333333";
export const DEFAULT_NAME_COLOR_DARK = "#C5C8C6";
export const LABEL_PADDING = 5;

export class NodeLabel extends shapes.standard.Rectangle {
  constructor(...args) {
    super(...args);
    this.on("change:position", function (label) {
      if (label.get("type") !== "NodeLabel") return;
      label.updateAlignment();
    });
  }

  defaults() {
    return {
      type: "NodeLabel",
      label: true,
      size: {
        width: 15,
        height: 15,
      },
      position: {
        x: 0,
        y: LABEL_PADDING,
      },
      attrs: {
        label: {
          text: "",
          pointerEvents: "all",
          fontSize: 10,
          fontWeight: "bold",
          fontFamily: PreferenceStore.INSTANCE.theme.model.text.fontFamily,
        },
        body: {
          pointerEvents: "none",
          opacity: 0,
        },
      },
    };
  }

  restrictTranslateToEntrySide() {
    const parent = this.getParentCell();
    if (!parent) return;

    const bbox = this.getBBox();
    const parentBBox = parent.getBBox().inflate(5).moveAndExpand({
      x: -bbox.width,
      y: -bbox.height,
      width: bbox.width,
      height: bbox.height,
    });

    return function (x, y) {
      const side = parentBBox.sideNearestToPoint({
        x,
        y,
      });
      switch (side) {
        case "right":
          return parentBBox.rightMiddle();
        case "left":
          return parentBBox.leftMiddle();
        case "bottom":
          return parentBBox.bottomMiddle();
        case "top":
          return parentBBox.topMiddle();
      }
    };
  }

  updateAlignment() {
    const parent = this.getParentCell();
    if (!parent) return;
    const bbox = this.getBBox();
    const parentBBox = parent.getBBox();
    const labelAttrs: attributes.SVGTextAttributes = {};
    // horizontal
    if (bbox.x < parentBBox.x) {
      labelAttrs.refX = bbox.width;
      labelAttrs.textAnchor = "end";
    } else if (bbox.x > parentBBox.x + parentBBox.width) {
      labelAttrs.refX = 0;
      labelAttrs.textAnchor = "start";
    } else {
      labelAttrs.refX = "50%";
      labelAttrs.textAnchor = "middle";
    }
    // vertical
    if (bbox.y < parentBBox.y) {
      labelAttrs.refY = bbox.height;
      labelAttrs.textVerticalAnchor = "bottom";
    } else if (bbox.y > parentBBox.y + parentBBox.height) {
      labelAttrs.refY = 0;
      labelAttrs.textVerticalAnchor = "top";
    } else {
      labelAttrs.refY = "50%";
      labelAttrs.textVerticalAnchor = "middle";
    }
    this.attr("label", labelAttrs);
  }

  toJSON() {
    const result = super.toJSON();
    return removeStaticAttributes(result);
  }
}

export class NodeLabelView extends dia.ElementView {}

export class LabeledNode extends BaseNode implements NamedElement {
  fitToChildren(opt?: {
    deep?: boolean;
    padding?: dia.Padding;
    expandOnly?: boolean;
    shrinkOnly?: boolean;
    terminator?: dia.Cell | dia.Cell.ID;
  }): this {
    // labels must not be considered for fitToChildren
    return this;
  }

  getName(): string {
    return this.getEmbeddedCells()[0]?.attr(["label", "text"]);
  }

  setName(name: string) {
    this.getEmbeddedCells()[0]?.attr(["label", "text"], name);
  }

  setNameFontSize(fontSize: string) {
    this.getEmbeddedCells()[0]?.attr(["label", "fontSize"], fontSize);
  }

  toJSON() {
    const result = super.toJSON();
    result.attrs = difference(result.attrs, this.defaults().attrs);
    return removeStaticAttributes(result);
  }

  unembed(cell: dia.Cell, opt?: dia.Graph.Options) {
    // do not allow unembedding of label
    return this;
  }

  createLabel(graph: dia.Graph) {
    const nameLabel = new NodeLabel();
    nameLabel.addTo(graph);
    this.embed(nameLabel);
    nameLabel.position(this.attributes.position.x, this.attributes.position.y + 15);
    nameLabel.updateAlignment();
  }
}

export class LabeledNodeView extends BaseView {
  setTheme(theme: string, opt?: { override?: boolean }): this {
    return super.setTheme(theme, opt);
  }
}

export class LabeledNodeController extends ElementController {
  onCellPointerDblClick(evt?: dia.Event) {
    const nodeLabel = this.view.model.getEmbeddedCells().find((c) => c instanceof NodeLabel);
    const nodeLabelView: NodeLabelView = this.context.paper.findViewByModel(nodeLabel);
    if (!nodeLabelView) return;

    deselectAll(this.context, true);

    const bbox = nodeLabelView.getBBox();
    const { x, y } = bbox.center();
    this.context.paper.trigger("cell:pointerdblclick", nodeLabelView, evt, x, y);
  }
  onElementDrop() {
    (this.view.model as LabeledNode).createLabel(this.context.graph);
  }
}

export class NodeLabelController extends ElementController {
  constructor(view: dia.CellView, context: RappidContext) {
    super(view, context);
  }

  onCellPointerDown(evt?: joint.dia.Event) {
    return;
  }

  onCellPointerDblClick(evt?: dia.Event) {
    this.context.nameTextEditor.create(this.view, "label");
  }
}

util.assign(shapes, {
  NodeLabel,
  NodeLabelView,
  NodeLabelController,
});
